import React, { useEffect } from "react";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useT } from "@/i18n/useT";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const t = useT();
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal } = useCart();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleCheckout = async () => {
    if (!items.length) return;
    try {
      const { data } = await api.post("/checkout-url", { items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })) });
      if (data.is_demo) toast(t.cart.demoNote);
      window.location.href = data.checkout_url;
    } catch { toast(t.cart.checkoutError); }
  };

  return (
    <>
      <div data-testid="cart-backdrop" onClick={() => setIsOpen(false)}
        className={`fixed inset-0 bg-espresso/30 z-50 transition-opacity duration-500 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      <aside data-testid="cart-drawer" aria-hidden={!isOpen}
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-cream border-l border-walnut/20 z-50 transition-transform duration-500 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-8 py-6 border-b border-walnut/20">
            <div className="font-serif text-xl text-espresso">{t.cart.yourBag}</div>
            <button data-testid="cart-close" onClick={() => setIsOpen(false)} className="text-espresso hover:text-walnut"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-8 py-6 br-no-scrollbar">
            {items.length === 0 ? (
              <div data-testid="cart-empty" className="text-center py-20 text-walnut text-sm font-light italic">{t.cart.empty}</div>
            ) : (
              <ul className="space-y-8">
                {items.map((i) => (
                  <li key={i.variant_id} data-testid={`cart-item-${i.variant_id}`} className="flex gap-4">
                    <div className="w-20 h-24 bg-walnut/10 overflow-hidden flex-shrink-0">
                      {i.image && <img src={i.image} alt={i.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="font-serif text-base text-espresso">{i.title}</div>
                      {i.variant_title && i.variant_title !== "Default" && <div className="text-xs text-walnut mt-0.5">{i.variant_title}</div>}
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center gap-2 border border-walnut/30">
                          <button onClick={() => updateQty(i.variant_id, i.quantity - 1)} className="p-1.5 hover:bg-walnut/10"><Minus className="w-3 h-3" /></button>
                          <span className="text-xs px-1 min-w-[20px] text-center">{i.quantity}</span>
                          <button onClick={() => updateQty(i.variant_id, i.quantity + 1)} className="p-1.5 hover:bg-walnut/10"><Plus className="w-3 h-3" /></button>
                        </div>
                        <div className="text-sm text-espresso">€{(i.price * i.quantity).toFixed(2)}</div>
                      </div>
                      <button onClick={() => removeItem(i.variant_id)} className="text-[11px] uppercase tracking-widest text-walnut hover:text-espresso mt-2 self-start">{t.cart.remove}</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {items.length > 0 && (
            <div className="px-8 py-6 border-t border-walnut/20 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-[0.25em] text-walnut">{t.cart.subtotal}</span>
                <span data-testid="cart-subtotal" className="font-serif text-xl text-espresso">€{subtotal.toFixed(2)}</span>
              </div>
              <button data-testid="checkout-button" onClick={handleCheckout} className="w-full bg-espresso text-cream py-4 text-xs uppercase tracking-[0.3em] hover:bg-dark-walnut transition-colors">{t.cart.checkout}</button>
              <div className="text-[11px] text-walnut/80 text-center">{t.cart.taxNote}</div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}